import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, MessageSquarePlus, Plus } from "lucide-react";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section } from "./_shared";

const AFFECTED = [
  { v: "api", l: "API" },
  { v: "database", l: "MongoDB" },
  { v: "email", l: "Kolejka e-mail" },
  { v: "backup", l: "Backup" },
  { v: "scheduler", l: "Zaplanowane zadania" },
  { v: "other", l: "Inne" },
];
const SEVERITY = [
  { v: "investigating", l: "🔍 Badamy" },
  { v: "identified", l: "🔧 Przyczyna zidentyfikowana" },
  { v: "monitoring", l: "👀 Monitorujemy" },
];

/**
 * Incident manager (Stripe/GitHub-style status transparency).
 *
 * Admin creates a row when an alert fires -> it appears on the public /status
 * page. Timeline updates are appended as investigation progresses. Resolving
 * the incident hides it from the "active" banner but keeps it in the 30-day
 * history section.
 */
export default function IncidentsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // New incident form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [affected, setAffected] = useState("other");
  const [severity, setSeverity] = useState("investigating");
  const [eta, setEta] = useState("");

  // Per-row update state
  const [openRow, setOpenRow] = useState(null); // id of incident being updated
  const [updateMsg, setUpdateMsg] = useState("");
  const [updateSev, setUpdateSev] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await teacherApi.get("/admin/incidents");
      setRows(data.incidents || []);
    } catch { toast.error("Nie udało się pobrać incydentów"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (title.trim().length < 3) { toast.error("Tytuł ≥3 znaki"); return; }
    try {
      await teacherApi.post("/admin/incidents", {
        title, description, affected, severity,
        eta_message: eta || null,
      });
      toast.success("Incydent utworzony · widoczny na /status");
      setTitle(""); setDescription(""); setEta(""); setSeverity("investigating"); setAffected("other");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd tworzenia"); }
  };

  const postUpdate = async (id) => {
    if (updateMsg.trim().length < 3) { toast.error("Wiadomość ≥3 znaki"); return; }
    try {
      await teacherApi.post(`/admin/incidents/${id}/update`, {
        message: updateMsg, severity: updateSev || null,
      });
      toast.success("Aktualizacja dodana");
      setUpdateMsg(""); setUpdateSev(""); setOpenRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd aktualizacji"); }
  };

  const resolve = async (id) => {
    const msg = window.prompt("Wiadomość rozwiązania (opcjonalnie):", "Incydent rozwiązany");
    if (msg === null) return;
    try {
      await teacherApi.post(`/admin/incidents/${id}/resolve`, { message: msg });
      toast.success("Incydent zamknięty");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd zamykania"); }
  };

  const openIncidents = rows.filter(r => r.status === "open");
  const resolvedIncidents = rows.filter(r => r.status === "resolved");

  return (
    <Section icon={AlertTriangle} title="Incydenty (transparencja statusu)">
      {/* Create form */}
      <div className="brut border-2 border-[#0F172A] rounded-md p-4 mb-6 bg-[#FEF08A]" data-testid="incident-create-card">
        <div className="font-black uppercase text-sm mb-3 flex items-center gap-2">
          <Plus className="w-4 h-4" strokeWidth={2.5} /> Nowy incydent
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder='Tytuł, np. "Opóźnienia w wysyłce maili verify"'
                 className="brut-input border-2 border-[#0F172A] rounded-md md:col-span-2"
                 data-testid="incident-title-input" />
          <Select value={affected} onValueChange={setAffected}>
            <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md h-10 font-bold" data-testid="incident-affected-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A]">
              {AFFECTED.map(a => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md h-10 font-bold" data-testid="incident-severity-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="brut bg-white border-2 border-[#0F172A]">
              {SEVERITY.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={eta} onChange={(e) => setEta(e.target.value)}
                 placeholder='Szacowany czas (np. "1-2h") — opcjonalne'
                 className="brut-input border-2 border-[#0F172A] rounded-md md:col-span-2"
                 data-testid="incident-eta-input" />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    placeholder="Opis dla użytkowników (widoczny publicznie)"
                    rows={2}
                    className="brut-input border-2 border-[#0F172A] rounded-md md:col-span-2"
                    data-testid="incident-description-input" />
        </div>
        <Button onClick={create} size="sm"
                className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="incident-create-btn">
          Utwórz incydent
        </Button>
        <span className="text-xs text-[#64748B] ml-3">
          ● Auto-monitoring co 15 min: SMTP kolejka {'>'} 100, backup {'>'} 72h, bounce {'>'} 10%
        </span>
        <Button
          onClick={async () => {
            try {
              await teacherApi.post("/admin/scheduler/run-auto-incident-check");
              toast.success("Auto-monitoring uruchomiony. Odświeżam listę…");
              load();
            } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
          }}
          size="sm"
          className="brut brut-hover bg-[#8B5CF6] text-white border-2 border-[#0F172A] font-bold rounded-md ml-2"
          data-testid="run-auto-monitor-btn"
        >
          🤖 Sprawdź teraz
        </Button>
        <Button
          onClick={async () => {
            try {
              await teacherApi.post("/admin/scheduler/test-incident-webhook");
              toast.success("Testowa wiadomość wysłana. Sprawdź kanał Slack/Discord.");
            } catch (e) { toast.error(e.response?.data?.detail || "Ustaw INCIDENT_WEBHOOK_URL w .env"); }
          }}
          size="sm"
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md ml-1"
          data-testid="test-webhook-btn"
        >
          💬 Test Slack/Discord
        </Button>
      </div>

      {/* Open incidents */}
      <div className="mb-6">
        <div className="font-black uppercase text-sm mb-2">🔴 Otwarte ({openIncidents.length})</div>
        {loading && <div className="text-sm text-[#64748B]">Ładowanie…</div>}
        {!loading && openIncidents.length === 0 && (
          <div className="brut border-2 border-[#0F172A] rounded-md p-3 bg-[#D1FAE5] text-sm font-bold" data-testid="incidents-empty">
            ✅ Brak aktywnych incydentów.
          </div>
        )}
        {openIncidents.map(inc => (
          <IncidentCard key={inc.id} inc={inc}
                        isEditing={openRow === inc.id}
                        onOpenUpdate={() => { setOpenRow(inc.id); setUpdateMsg(""); setUpdateSev(""); }}
                        onCloseUpdate={() => setOpenRow(null)}
                        updateMsg={updateMsg} setUpdateMsg={setUpdateMsg}
                        updateSev={updateSev} setUpdateSev={setUpdateSev}
                        onPostUpdate={() => postUpdate(inc.id)}
                        onResolve={() => resolve(inc.id)} />
        ))}
      </div>

      {/* Resolved history */}
      <div>
        <div className="font-black uppercase text-sm mb-2">
          ✅ Rozwiązane (ostatnie 90 dni · {resolvedIncidents.length})
        </div>
        {resolvedIncidents.length === 0 && (
          <div className="text-xs text-[#64748B]">Brak historii.</div>
        )}
        {resolvedIncidents.map(inc => (
          <div key={inc.id}
               className="brut border-2 border-[#0F172A] rounded-md p-3 mb-2 bg-[#F1F5F9] text-sm"
               data-testid={`incident-resolved-${inc.id}`}>
            <div className="flex justify-between items-center">
              <b>{inc.title}</b>
              <span className="text-xs text-[#64748B]">
                {new Date(inc.created_at).toLocaleDateString("pl")} → {new Date(inc.resolved_at).toLocaleDateString("pl")}
              </span>
            </div>
            <div className="text-xs mt-1">{AFFECTED.find(a => a.v === inc.affected)?.l} · {inc.updates.length} aktualizacj{inc.updates.length === 1 ? "a" : "i"}</div>
          </div>
        ))}
      </div>
    </Section>
  );
}


function IncidentCard({ inc, isEditing, onOpenUpdate, onCloseUpdate,
                       updateMsg, setUpdateMsg, updateSev, setUpdateSev,
                       onPostUpdate, onResolve }) {
  const affectedLabel = AFFECTED.find(a => a.v === inc.affected)?.l || inc.affected;
  const sevLabel = SEVERITY.find(s => s.v === inc.severity)?.l || inc.severity;

  return (
    <div className="brut border-2 border-[#0F172A] rounded-md p-4 mb-3 bg-[#FEE2E2]"
         data-testid={`incident-open-${inc.id}`}>
      <div className="flex justify-between items-start flex-wrap gap-2 mb-2">
        <div>
          <div className="font-black text-base flex items-center gap-2">
            {inc.auto && (
              <span
                className="brut border-2 border-[#0F172A] rounded px-1.5 py-0.5 text-[10px] font-black bg-[#8B5CF6] text-white"
                data-testid={`incident-auto-badge-${inc.id}`}
                title="Utworzone automatycznie przez monitoring"
              >
                🤖 AUTO
              </span>
            )}
            {inc.title}
          </div>
          <div className="text-xs mt-0.5">
            <b>{affectedLabel}</b> · {sevLabel}
            {inc.eta_message && <> · ⏱ ETA: <b>{inc.eta_message}</b></>}
            <> · rozpoczęte: {new Date(inc.created_at).toLocaleString("pl")}</>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={isEditing ? onCloseUpdate : onOpenUpdate}
                  className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-xs"
                  data-testid={`incident-add-update-btn-${inc.id}`}>
            <MessageSquarePlus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
            {isEditing ? "Anuluj" : "Aktualizacja"}
          </Button>
          <Button size="sm" onClick={onResolve}
                  className="brut brut-hover bg-[#86EFAC] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                  data-testid={`incident-resolve-btn-${inc.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />
            Rozwiąż
          </Button>
        </div>
      </div>

      {inc.description && (
        <div className="text-sm mb-2 text-[#334155]">{inc.description}</div>
      )}

      {isEditing && (
        <div className="brut border-2 border-[#0F172A] rounded p-2 bg-white mb-2 space-y-2" data-testid={`incident-update-form-${inc.id}`}>
          <Textarea value={updateMsg} onChange={(e) => setUpdateMsg(e.target.value)}
                    placeholder="Nowa wiadomość dla użytkowników (widoczna publicznie)"
                    rows={2}
                    className="brut-input border-2 border-[#0F172A] rounded-md text-sm"
                    data-testid={`incident-update-msg-${inc.id}`} />
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={updateSev} onValueChange={setUpdateSev}>
              <SelectTrigger className="brut border-2 border-[#0F172A] rounded-md h-9 font-bold text-xs w-[240px]" data-testid={`incident-update-sev-${inc.id}`}>
                <SelectValue placeholder="Zmień severity (opcjonalnie)" />
              </SelectTrigger>
              <SelectContent className="brut bg-white border-2 border-[#0F172A]">
                {SEVERITY.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" onClick={onPostUpdate}
                    className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] font-bold rounded-md text-xs"
                    data-testid={`incident-submit-update-btn-${inc.id}`}>
              Dodaj aktualizację
            </Button>
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="text-xs space-y-1 pl-4 border-l-2 border-[#0F172A]/30">
        {inc.updates.slice().reverse().map((u, i) => (
          <div key={i} className="text-[#334155]">
            <span className="font-bold">{new Date(u.ts).toLocaleString("pl")}</span>
            {u.severity && <> · <b>{SEVERITY.find(s => s.v === u.severity)?.l || u.severity}</b></>}
            <> — {u.message}</>
          </div>
        ))}
      </div>
    </div>
  );
}
